import { useState } from "react";
import { Loader2, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { useT } from "@/i18n";
import { TID } from "@/constants/testIds";
import DotHalo from "@/components/DotHalo";
import { PoweredBy } from "@/components/PoweredBy";

const FIELD =
  "h-[54px] w-full rounded-[14px] border bg-white px-4 text-[17px] text-[#1D1D1F] outline-none transition-all placeholder:text-[#B0B0B8] focus:border-[#0A66FF] focus:ring-[3px] focus:ring-[#0A66FF]/20";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useT();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("login"); // "login" | "forgot"
  const [forgotUser, setForgotUser] = useState("");
  const [forgotDone, setForgotDone] = useState(false);

  const ready = username.trim() && password;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(username, password);
      navigate("/");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/forgot", { username: forgotUser.trim() });
      setForgotDone(true);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const goForgot = () => {
    setError("");
    setForgotDone(false);
    setForgotUser("");
    setMode("forgot");
  };
  const goLogin = () => {
    setError("");
    setMode("login");
  };

  return (
    <div className="relative flex min-h-screen items-start justify-center bg-white px-6 py-14 sm:items-center sm:py-16">
      <div className="w-full max-w-[460px]">
        <div className="flex justify-center">
          <DotHalo>
            <img
              src="/meranggi-logo.png"
              alt="Meranggi"
              className="h-[96px] w-[96px] rounded-full object-cover"
            />
          </DotHalo>
        </div>

        {mode === "login" ? (
          <>
            <h1 className="fade-up mt-4 text-center text-[22px] font-bold leading-[1.15] tracking-[-0.02em] text-[#1D1D1F] sm:text-[28px]">
              {t("login.title")}
            </h1>

            <form
              data-testid={TID.login.form}
              onSubmit={submit}
              className="fade-up mt-10 space-y-3"
              style={{ animationDelay: "80ms" }}
            >
              <input
                data-testid={TID.login.email}
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoComplete="username"
                autoFocus
                placeholder={t("login.username")}
                className={`${FIELD} border-[#D2D2D7]`}
              />
              <input
                data-testid={TID.login.password}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                placeholder={t("login.password")}
                className={`${FIELD} border-[#D2D2D7]`}
              />

              {error && (
                <p
                  data-testid={TID.login.error}
                  className="fade-up rounded-[14px] bg-[#FDF0F2] px-4 py-3 text-[14.5px] text-[#C8102E]"
                >
                  {error}
                </p>
              )}

              <button
                type="submit"
                data-testid={TID.login.submit}
                disabled={busy || !ready}
                className={`mt-3 flex h-[54px] w-full items-center justify-center gap-2 rounded-[14px] text-[17px] font-medium text-white transition-all duration-200 ${
                  ready
                    ? "bg-[#0071E3] hover:bg-[#0077ED] active:scale-[0.99]"
                    : "cursor-not-allowed bg-[#0071E3]/30"
                }`}
              >
                {busy && <Loader2 className="h-[18px] w-[18px] animate-spin" />}
                {t("login.signIn")}
              </button>

              <button
                type="button"
                data-testid={TID.login.forgotLink}
                onClick={goForgot}
                className="mt-2 w-full text-center text-[15px] font-medium text-[#0071E3] hover:underline"
              >
                {t("login.forgot")}
              </button>
            </form>
          </>
        ) : (
          <>
            <h1 className="fade-up mt-4 text-center text-[22px] font-bold leading-[1.15] tracking-[-0.02em] text-[#1D1D1F] sm:text-[28px]">
              {t("login.resetTitle")}
            </h1>

            {forgotDone ? (
              <div
                data-testid={TID.login.forgotDone}
                className="fade-up mt-8 flex flex-col items-center gap-3 text-center"
              >
                <CheckCircle2 className="h-12 w-12 text-[#34C759]" />
                <p className="text-[16px] text-[#1D1D1F]">
                  {t("login.resetDone")}
                </p>
                <button
                  type="button"
                  data-testid={TID.login.backLink}
                  onClick={goLogin}
                  className="mt-2 text-[15px] font-medium text-[#0071E3] hover:underline"
                >
                  {t("login.backSignIn")}
                </button>
              </div>
            ) : (
              <form onSubmit={submitForgot} className="fade-up mt-10 space-y-3" style={{ animationDelay: "80ms" }}>
                <p className="px-1 text-[14.5px] text-[#6E6E73]">
                  {t("login.resetHint")}
                </p>
                <input
                  data-testid={TID.login.forgotInput}
                  type="text"
                  value={forgotUser}
                  onChange={(e) => setForgotUser(e.target.value)}
                  required
                  autoFocus
                  placeholder={t("login.userOrEmail")}
                  className={`${FIELD} border-[#D2D2D7]`}
                />

                {error && (
                  <p
                    data-testid={TID.login.error}
                    className="fade-up rounded-[14px] bg-[#FDF0F2] px-4 py-3 text-[14.5px] text-[#C8102E]"
                  >
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  data-testid={TID.login.forgotSubmit}
                  disabled={busy || !forgotUser.trim()}
                  className={`mt-3 flex h-[54px] w-full items-center justify-center gap-2 rounded-[14px] text-[17px] font-medium text-white transition-all duration-200 ${
                    forgotUser.trim()
                      ? "bg-[#0071E3] hover:bg-[#0077ED] active:scale-[0.99]"
                      : "cursor-not-allowed bg-[#0071E3]/30"
                  }`}
                >
                  {busy && <Loader2 className="h-[18px] w-[18px] animate-spin" />}
                  {t("login.sendReset")}
                </button>

                <button
                  type="button"
                  data-testid={TID.login.backLink}
                  onClick={goLogin}
                  className="mt-2 w-full text-center text-[15px] font-medium text-[#0071E3] hover:underline"
                >
                  {t("login.backSignIn")}
                </button>
              </form>
            )}
          </>
        )}
      </div>
      <PoweredBy className="absolute inset-x-0 bottom-0" />
    </div>
  );
}
